import {
  ICON_BULLHORN_OUTLINE, ICON_CALENDAR_OUTLINE, ICON_DOMAIN, ICON_FLAG_OUTLINE,
} from '@doveit/bricks';
import { CanvaResourceType } from '../providers/api/dtos';
import type { MarketingOpportunityData, MarketingOpportunityTriggerData } from './types';

export const CANVA_CARD_ASPECT_RATIO = 3 / 2;

export const CANVA_BRAND_TEMPLATES_URL = 'https://www.canva.com/brand/brand-templates';

export const CANVA_URL = 'https://www.canva.com';

export const CANVA_ROOT_FOLDER_ID = 'root';
export const CANVA_HQ_FOLDER_ID = process.env.REACT_APP_CANVA_HQ_FOLDER_ID || 'FAHMp5I78wI';
export const CANVA_SALES_FOLDER_ID = process.env.REACT_APP_CANVA_SALES_FOLDER_ID || 'FAHKpwM2k9g';

export const AVAILABLE_FOLDERS_BY_USER_ROLE = {
  default: {
    default: CANVA_ROOT_FOLDER_ID,
    available: [],
  },
  agent: {
    default: CANVA_SALES_FOLDER_ID,
    available: [{ id: CANVA_SALES_FOLDER_ID, name: 'Cartelle Sales' }],
  },
  marketing: {
    default: CANVA_HQ_FOLDER_ID,
    available: [
      { id: CANVA_HQ_FOLDER_ID, name: 'Cartelle HQ' },
      { id: CANVA_SALES_FOLDER_ID, name: 'Cartelle Sales' },
    ],
  },
};

export const canvaResourceTypeLabels: Record<CanvaResourceType, {
  singular: string,
  plural: string
}> = {
  brand_template: {
    singular: 'Template',
    plural: 'Template',
  },
  design: {
    singular: 'Design',
    plural: 'Design',
  },
  folder: {
    singular: 'Cartella',
    plural: 'Cartelle',
  },
};

export const POST_4_5_OPEN_HOUSE_CANVA_TEMPLATE_ID = 'EAHKNQiKVdQ';
export const POST_4_5_IMMOBILE_VENDUTO_CANVA_TEMPLATE_ID = 'EAHKBhQema4';
export const POST_4_5_IMMOBILE_IN_VENDITA_CANVA_TEMPLATE_ID = 'EAHKBkY_QdU';
export const LOCANDINA_A4_ORIZZONTALE_IMMOBILE_IN_VENDITA_CANVA_STANDARD_AGENZIE_CANVA_TEMPLATE_ID = 'EAHPXvXdpog';
export const LOCANDINA_A4_VERTICALE_IMMOBILE_IN_VENDITA_CANVA_STANDARD_AGENZIE_CANVA_TEMPLATE_ID = 'EAHPXhnS_Cw';
export const VOLANTINO_A5_IMMOBILE_IN_VENDITA_CANVA_TEMPLATE_ID = 'EAHKBWwK-wY';
export const VOLANTINO_A5_IMMOBILE_VENDUTO_CANVA_TEMPLATE_ID = 'EAHNTyd3V9I';

export const MARKETING_OPPORTUNITIES = {
  'open-house-post': {
    icon: ICON_CALENDAR_OUTLINE,
    title: 'Genera post open-house',
    description: 'aumenta la visibilità dell\'evento e dell\'immobile con un post social.',
    templates: [
      POST_4_5_OPEN_HOUSE_CANVA_TEMPLATE_ID,
    ],
  },
  'property-sold-post': {
    icon: ICON_FLAG_OUTLINE,
    title: 'Genera post celebrativo',
    description: 'condividi questo risultato per mostrare che hai portato a termine la compravendita.',
    templates: [
      POST_4_5_IMMOBILE_VENDUTO_CANVA_TEMPLATE_ID,
    ],
  },
  'property-sold-flyer': {
    icon: ICON_FLAG_OUTLINE,
    title: 'Genera volantino',
    description: 'pubblicizza la vendita dell\'immobile in zona per attirare nuovi venditori.',
    templates: [
      VOLANTINO_A5_IMMOBILE_VENDUTO_CANVA_TEMPLATE_ID,
    ],
  },
  'property-live-post': {
    icon: ICON_BULLHORN_OUTLINE,
    title: 'Genera post promozionale',
    description: 'raggiungi i potenziali acquirenti sui social con un post dedicato all\'immobile in vendita.',
    templates: [VOLANTINO_A5_IMMOBILE_VENDUTO_CANVA_TEMPLATE_ID],
  },
  'property-live-flyer': {
    icon: ICON_BULLHORN_OUTLINE,
    title: 'Genera volantino',
    description: 'pubblicizza l\'immobile in zona per attirare nuovi venditori.',
    templates: [
      VOLANTINO_A5_IMMOBILE_IN_VENDITA_CANVA_TEMPLATE_ID,
    ],
  },
  'property-live-poster': {
    icon: ICON_BULLHORN_OUTLINE,
    title: 'Genera locandina',
    description: 'metti in bella vista l\'immobile nella vetrina della tua agenzia.',
    templates: [
      LOCANDINA_A4_ORIZZONTALE_IMMOBILE_IN_VENDITA_CANVA_STANDARD_AGENZIE_CANVA_TEMPLATE_ID,
      LOCANDINA_A4_VERTICALE_IMMOBILE_IN_VENDITA_CANVA_STANDARD_AGENZIE_CANVA_TEMPLATE_ID,
    ],
  },
  'property-recently-published-post': {
    icon: ICON_BULLHORN_OUTLINE,
    title: 'Genera post di lancio',
    description: 'è il primo passo per raggiungere potenziali acquirenti sui social.',
    templates: [
      POST_4_5_IMMOBILE_IN_VENDITA_CANVA_TEMPLATE_ID,
    ],
  },
} as const satisfies Record<string, MarketingOpportunityData>;

export const MARKETING_TRIGGERS = {
  'property-sold': {
    icon: ICON_DOMAIN,
    title: 'L\'immobile è venduto',
    opportunities: ['property-sold-post', 'property-sold-flyer'],
  },
  'property-live': {
    icon: ICON_DOMAIN,
    title: 'L\'immobile è stato pubblicato su Dove.it',
    opportunities: ['property-live-post', 'property-live-flyer', 'property-live-poster', 'open-house-post'],
  },
  'property-recently-published': {
    icon: ICON_DOMAIN,
    title: 'L\'immobile è stato pubblicato di recente su Dove.it',
    opportunities: ['property-recently-published-post', 'property-live-flyer', 'property-live-poster', 'open-house-post'],
  },
} as const satisfies Record<string, MarketingOpportunityTriggerData>;

export const BRAND_TEMPLATE_AUTOFILL_PROPERTY_ID_PARAM = 'propertyId';
export const BRAND_TEMPLATE_AUTOFILL_AGENT_ID_PARAM = 'agentId';
export const BRAND_TEMPLATE_AUTOFILL_AGENCY_ID_PARAM = 'agencyId';

export const BRAND_TEMPLATE_AUTOFILL_PARAMS = [
  BRAND_TEMPLATE_AUTOFILL_PROPERTY_ID_PARAM,
  BRAND_TEMPLATE_AUTOFILL_AGENT_ID_PARAM,
  BRAND_TEMPLATE_AUTOFILL_AGENCY_ID_PARAM,
] as const;
